import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useBranding } from '../Context/BrandingContext';
import { getSessionToken } from '../Services/services';
import { useLoginModal } from '../Context/LoginModalContext';
import AppHeader from './AppHeader';

function IconClock({ color, size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function IconWrench({ color, size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

function IconSearch({ color, size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function IconShieldCheck({ color, size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function trimCompanyName(name) {
  return (name || '').trim();
}

function Landing({ isTenant = false }) {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { openLogin } = useLoginModal();
  const [keyword, setKeyword] = useState('');

  const companyName = trimCompanyName(branding.companyName);
  const hasCompanyName = companyName.length > 0;

  const valueProps = useMemo(() => {
    const v = companyName;
    return [
      {
        key: 'inventory',
        title: 'Real-Time Inventory',
        desc: hasCompanyName
          ? `Up-to-date availability from ${v}'s stock and locations`
          : 'Up-to-date availability from live stock and locations',
        icon: IconClock,
      },
      {
        key: 'mro',
        title: 'Repair Management',
        desc: hasCompanyName
          ? `Submit and track MRO work orders with ${v}'s team`
          : 'Submit and track MRO work orders with the operations team',
        icon: IconWrench,
      },
      {
        key: 'rfq',
        title: 'Online RFQs',
        desc: hasCompanyName
          ? `Submit quote requests through the ${v} portal and get responses without phone tag`
          : 'Submit quote requests through the portal and get responses without phone tag',
        icon: IconSearch,
      },
      {
        key: 'secure',
        title: 'Secure Transactions',
        desc: hasCompanyName
          ? `Clear, auditable records for orders and RFQs placed directly with ${v}`
          : 'Clear, auditable records for orders and RFQs placed through the portal',
        icon: IconShieldCheck,
      },
    ];
  }, [companyName, hasCompanyName]);

  const isAuthenticated = !!getSessionToken();

  const getExternalLoginUrl = () => {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes('dev.avsight-ai.com') || hostname === 'dev.avsight-ai.com') {
      return 'https://dev.avsight-ai.com';
    }
    if (hostname.includes('stgn.avsight-ai.com') || hostname === 'stgn.avsight-ai.com') {
      return 'https://stgn.avsight-ai.com';
    }
    return 'https://avsight-ai.com';
  };

  const handleSignIn = () => {
    if (isTenant) {
      openLogin({ redirectPath: `${window.location.pathname}${window.location.search || ''}` });
    } else {
      window.location.href = getExternalLoginUrl();
    }
  };

  const handleSearch = () => {
    const nextKeyword = (keyword || '').trim();
    const params = new URLSearchParams();
    if (nextKeyword) params.set('keyword', nextKeyword);
    navigate(`/catalog?${params.toString()}`);
  };

  const primary = branding.primaryColor;

  return (
    <div style={styles.page}>
      <AppHeader variant="light" />

      <main style={styles.main}>
        <section style={styles.heroSection}>
          <h1 style={styles.heroTitle}>Aviation Parts & MRO — Simplified</h1>
          <p style={styles.heroLead}>
            {hasCompanyName
              ? `Search ${companyName}'s live catalog, request quotes, and manage repairs in one customer portal — you work directly with ${companyName}, backed by AvSight.`
              : 'Search the live catalog, request quotes, and manage repairs in one customer portal — backed by AvSight.'}
          </p>

          {isTenant && (
            <>
              <div style={styles.searchCard}>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                  placeholder="Search part number, ATA, keyword…"
                  style={styles.searchInput}
                  aria-label="Search inventory"
                />
                <button
                  type="button"
                  onClick={handleSearch}
                  style={{ ...styles.searchButton, backgroundColor: primary }}
                >
                  <IconSearch color="#fff" size={18} />
                  <span>Search</span>
                </button>
              </div>
              <p style={styles.searchHint}>
                {hasCompanyName
                  ? `Browse ${companyName}'s inventory anonymously. Login is required for RFQs and orders.`
                  : 'Browse inventory anonymously. Login is required for RFQs and orders.'}
              </p>
            </>
          )}
        </section>

        <section style={styles.valueSection}>
          <div style={styles.valueGrid}>
            {valueProps.map(({ key, title, desc, icon: Icon }) => (
              <div key={key} style={styles.valueCard}>
                <div style={styles.valueIconWrap}>
                  <Icon color={primary} size={24} />
                </div>
                <div style={styles.valueCardTitle}>{title}</div>
                <div style={styles.valueCardDesc}>{desc}</div>
              </div>
            ))}
          </div>
        </section>

        <section style={{ ...styles.ctaBand, backgroundColor: primary }}>
          <h2 style={styles.ctaTitle}>
            {hasCompanyName
              ? `Request access to the ${companyName} customer portal`
              : 'Request access to the customer portal'}
          </h2>
          <p style={styles.ctaSubtitle}>
            {hasCompanyName
              ? `Browse ${companyName}'s catalog, submit RFQs, and place orders online — the same parts and team you already rely on, without a multi-supplier marketplace.`
              : 'Browse the catalog, submit RFQs, and place orders online — the same parts and team you already rely on, without a multi-supplier marketplace.'}
          </p>
          {isAuthenticated ? (
            <button
              type="button"
              onClick={() => navigate('/catalog')}
              style={{ ...styles.ctaButton, color: primary }}
            >
              Browse catalog
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSignIn}
              style={{ ...styles.ctaButton, color: primary }}
            >
              Request access
            </button>
          )}
        </section>
      </main>

      <footer style={styles.footer}>
        <span>
          © {new Date().getFullYear()}
          {hasCompanyName ? ` ${companyName}` : ''}
          {' · '}
          <Link to="/privacy" style={styles.footerLink}>
            Privacy Policy
          </Link>
          {' · '}
          <Link to="/health" style={styles.footerLink}>
            System Status
          </Link>
        </span>
      </footer>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f9fafb',
    color: '#111827',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  main: {
    flex: 1,
  },
  heroSection: {
    textAlign: 'center',
    padding: '64px 24px 48px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  heroTitle: {
    fontSize: 'clamp(2rem, 5vw, 3rem)',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 24px 0',
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
  },
  heroLead: {
    fontSize: '18px',
    lineHeight: 1.6,
    color: '#4b5563',
    margin: '0 auto 40px',
    maxWidth: '42rem',
  },
  searchCard: {
    maxWidth: '48rem',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.06)',
    padding: '24px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    alignItems: 'stretch',
  },
  searchInput: {
    flex: '1 1 220px',
    minWidth: '200px',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '12px 16px',
    fontSize: '16px',
    outline: 'none',
  },
  searchButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: 'none',
    borderRadius: '12px',
    color: '#ffffff',
    fontWeight: '700',
    fontSize: '15px',
    padding: '12px 24px',
    cursor: 'pointer',
    flex: '0 0 auto',
  },
  searchHint: {
    marginTop: '16px',
    fontSize: '14px',
    color: '#6b7280',
  },
  valueSection: {
    padding: '0 32px 64px',
    maxWidth: '1152px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  valueGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '24px',
  },
  valueCard: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
    padding: '24px',
    textAlign: 'left',
  },
  valueIconWrap: {
    marginBottom: '12px',
  },
  valueCardTitle: {
    fontWeight: '600',
    fontSize: '16px',
    color: '#111827',
    marginBottom: '8px',
  },
  valueCardDesc: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#4b5563',
  },
  ctaBand: {
    textAlign: 'center',
    padding: '64px 24px',
    color: '#ffffff',
  },
  ctaTitle: {
    fontSize: 'clamp(1.5rem, 3vw, 1.875rem)',
    fontWeight: '600',
    margin: '0 0 16px 0',
  },
  ctaSubtitle: {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.9)',
    margin: '0 auto 24px',
    maxWidth: '36rem',
    lineHeight: 1.5,
  },
  ctaButton: {
    backgroundColor: '#ffffff',
    border: 'none',
    borderRadius: '16px',
    padding: '14px 32px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
  },
  footer: {
    textAlign: 'center',
    padding: '24px 16px',
    fontSize: '14px',
    color: '#6b7280',
    backgroundColor: '#f9fafb',
  },
  footerLink: {
    color: '#6b7280',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
};

export default Landing;
